import { convertFromRaw } from 'draft-js'
import { createReducer } from 'redux-create-reducer'
import type {
  Campaign,
  CampaignSection,
  ImageProps,
  TextElement,
} from 'types/content-editor'
import {
  CAMPAIGN_EDITOR_DELETE_SECTION,
  CAMPAIGN_EDITOR_DUPLICATE_SECTION,
  CAMPAIGN_EDITOR_EDIT_MODE_SWITCH,
  CAMPAIGN_EDITOR_INSERT_SECTION_BLOCK,
  CAMPAIGN_EDITOR_LOAD,
  CAMPAIGN_EDITOR_MOVE_SECTION,
  CAMPAIGN_EDITOR_PLAYLIST_BLOCK_CHANGE,
  CAMPAIGN_EDITOR_TEXT_BLOCK_CHANGE,
  CAMPAIGN_EDITOR_UPDATE_GLOBAL_PROPS,
  CAMPAIGN_EDITOR_UPDATE_SECTION,
  CAMPAIGN_LIST_BEGIN_EDIT,
} from '@disco/constants/ActionTypes'
import { ENTITY_TYPE } from '@disco/constants/content-editor'
import { getMaxImageWidth } from '@disco/content-editor/editor-utils'
import exportToHtml from '@disco/content-editor/html-export'
import {
  traverseSections,
  updateBlock,
  updateColumn,
} from '@disco/content-editor/section'
import { moveItems, updateItem } from '@disco/utils/immutable'
const initialState: Campaign = {
  sections: [],
  generalBackground: '#fff',
  fontSize: '13',
  verticalPadding: '12',
  horizontalPadding: '25',
}

const getAdjustedElement = (
  image: ImageProps,
  section: CampaignSection,
  oldState: Campaign,
  newState: Campaign,
): ImageProps | null | undefined => {
  if (!image.width) return null
  const oldMaxWidth = getMaxImageWidth(section, oldState)
  const newMaxWidth = getMaxImageWidth(section, newState)
  const elementWidth = parseInt(image.width, 10)
  const needToAdjust =
    oldMaxWidth === elementWidth || elementWidth > newMaxWidth

  if (needToAdjust) {
    return {
      ...image,
      width: newMaxWidth.toString(10),
      height: Math.floor(newMaxWidth / (image.aspectRatio || 1)).toString(10),
    }
  }

  return null
}

const modifyTextElement = (
  element: TextElement,
  section: CampaignSection,
  state: Campaign,
  newState: Campaign,
): TextElement => {
  if (!element.content || !element.content.entityMap) return element
  const newContent = {
    ...element.content,
    entityMap: Object.keys(element.content.entityMap).reduce((acc, key) => {
      const entity = element.content ? element.content.entityMap[key] : null

      if (entity && entity.getType() === ENTITY_TYPE.IMAGE) {
        const adjustedData = getAdjustedElement(
          entity.data,
          section,
          state,
          newState,
        )
        return {
          ...acc,
          [key]: { ...entity, data: adjustedData ? adjustedData : entity.data },
        }
      }

      return { ...acc, [key]: entity }
    }, {}),
  }
  return {
    ...element,
    content: newContent,
    htmlContent: element.content
      ? exportToHtml(convertFromRaw(newContent))
      : element.htmlContent,
  }
}

const updateSection = (
  state: Campaign,
  sectionIndex: number,
  updatedSection: CampaignSection,
): Campaign => {
  return {
    ...state,
    sections: updateItem(state.sections, sectionIndex, updatedSection),
  }
}

const updateTargetBlock = (
  state: Campaign,
  sectionIndex: number,
  columnIndex: number,
  contentIndex: number,
  update: Record<string, any>,
): Campaign => {
  // sectionIndex comes from the action payload for an existing section
  const targetSection = state.sections[sectionIndex]!
  // columnIndex comes from the action payload for an existing column
  const targetColumn = targetSection.columns[columnIndex]!

  if (!targetColumn.columnContent) {
    throw new Error('columnContent is undefined')
  }

  // contentIndex comes from the action payload for an existing block
  const targetBlock = targetColumn.columnContent[contentIndex]!
  const updatedBlock = { ...targetBlock, ...update }
  const updatedColumn = updateBlock(targetColumn, contentIndex, updatedBlock)
  const updatedSection = updateColumn(targetSection, columnIndex, updatedColumn)
  return updateSection(state, sectionIndex, updatedSection)
}

export const campaignEditorReducer = createReducer<Campaign, any>(
  initialState,
  {
    [CAMPAIGN_LIST_BEGIN_EDIT](_state, { campaign, campaignEditor }) {
      return { ...campaignEditor, id: campaign.id }
    },

    [CAMPAIGN_EDITOR_LOAD](state, { sections }) {
      return { ...state, sections }
    },

    [CAMPAIGN_EDITOR_DUPLICATE_SECTION](state, { sectionIndex }) {
      // sectionIndex comes from the action payload for an existing section
      const section = state.sections[sectionIndex]!
      return {
        ...state,
        sections: [
          ...state.sections.slice(0, sectionIndex),
          section,
          { ...section, editMode: false },
          ...state.sections.slice(sectionIndex + 1),
        ],
      }
    },

    [CAMPAIGN_EDITOR_DELETE_SECTION](state, { sectionIndex }) {
      return {
        ...state,
        sections: [
          ...state.sections.slice(0, sectionIndex),
          ...state.sections.slice(sectionIndex + 1),
        ],
      }
    },

    [CAMPAIGN_EDITOR_INSERT_SECTION_BLOCK](state, { sectionIndex, section }) {
      return {
        ...state,
        sections: [
          ...state.sections.slice(0, sectionIndex),
          section,
          ...state.sections.slice(sectionIndex),
        ],
      }
    },

    [CAMPAIGN_EDITOR_UPDATE_SECTION](state, { sectionIndex, section }) {
      return {
        ...state,
        sections: updateItem(state.sections, sectionIndex, section),
      }
    },

    [CAMPAIGN_EDITOR_TEXT_BLOCK_CHANGE](
      state,
      { sectionIndex, columnIndex, contentIndex, text, html },
    ) {
      return updateTargetBlock(state, sectionIndex, columnIndex, contentIndex, {
        content: text,
        htmlContent: html,
      })
    },

    [CAMPAIGN_EDITOR_PLAYLIST_BLOCK_CHANGE](
      state,
      { sectionIndex, columnIndex, contentIndex, playlistId },
    ) {
      return updateTargetBlock(state, sectionIndex, columnIndex, contentIndex, {
        playlist_id: playlistId,
      })
    },

    [CAMPAIGN_EDITOR_EDIT_MODE_SWITCH](state, { sectionIndex }) {
      return {
        ...state,
        sections: state.sections.map((s, sIndex) => {
          return {
            ...s,
            editMode: sIndex === sectionIndex ? !s.editMode : false,
          }
        }),
      }
    },

    [CAMPAIGN_EDITOR_MOVE_SECTION](
      state,
      { sourceSectionIndex, targetSectionIndex },
    ) {
      return {
        ...state,
        sections: moveItems(
          state.sections,
          [sourceSectionIndex],
          targetSectionIndex,
        ),
      }
    },

    [CAMPAIGN_EDITOR_UPDATE_GLOBAL_PROPS](state, { props }) {
      if (
        props.horizontalPadding &&
        state.horizontalPadding !== props.horizontalPadding
      ) {
        const newState = { ...state, ...props }
        return {
          ...state,
          ...props,
          sections: traverseSections(state.sections, (element, section) => {
            if (element.type === 'image') {
              const adjustedElement = getAdjustedElement(
                element,
                section,
                state,
                newState,
              )
              return adjustedElement ? adjustedElement : element
            }

            if (element.type === 'text') {
              return modifyTextElement(element, section, state, newState)
            }

            return element
          }),
        }
      }

      return { ...state, ...props }
    },
  },
)
